<template>
  <div
    class="login-page"
    :class="{ 'login-page--popup': isPopup }"
    :style="isPopup ? null : { backgroundImage: 'url(/img/cityscape.webp)' }"
  >
    <div v-if="!isPopup" class="login-page__bar">
      <a class="login-page__home" href="/" aria-label="Back to iCare Connect home">← Home</a>
      <theme-toggle @change="onThemeChange" />
    </div>

    <div class="login-shell">
      <aside class="login-brand">
        <div class="login-brand__inner">
          <p class="login-brand__eyebrow">iCare Connect</p>
          <h1 class="login-brand__headline">{{ brandHeadline }}</h1>
          <p class="login-brand__lead">{{ brandLead }}</p>
          <ul class="login-brand__features">
            <li v-for="(item, i) in brandFeatures" :key="i">{{ item }}</li>
          </ul>
          <p class="login-brand__trust">{{ brandNote }}</p>
        </div>
      </aside>

      <section class="login-panel">
        <div v-if="domains.length > 0" class="domain-selection">
          <div class="domain-header">
            <h4>Select Your Organization</h4>
            <p>{{ message }}</p>
            <b-button size="sm" variant="light" @click="loginCancel()">
              Back to Sign In
            </b-button>
          </div>
          <div class="domain-list">
            <tenant-card
              v-for="item in domains"
              :key="item.domain || item.name"
              :domain="item"
              @selectDomain="selectDomain"
            />
          </div>
        </div>
        <login-card
          v-else
          @signin="signIn($event)"
          @forgot-password="forgotPassword($event)"
          @change-password="changePassword($event)"
          @screen-change="loginScreen = $event"
          :domain="resolvedDomain"
          :logo="logo"
          :initialScreen="loginScreen"
          :askSignupCode="askSignupCode"
        />
      </section>
    </div>
  </div>
</template>
<script>

import AuthenticationService from "@/services/AuthenticationService";
import LoginCard             from "@/components/LoginCard.vue";
import TenantCard            from "@/components/TenantCard.vue";
import ThemeToggle           from "@/components/ThemeToggle.vue";
import { initTheme }         from "@/lib/theme";
import { goToApp }           from "@/lib/session";

export default {
  name: "Login",
  components: {
    LoginCard,
    TenantCard,
    ThemeToggle,
  },
  props: {
    isPopup        : { type: Boolean, default: false },
    title          : { type: String, default: "" },
    logo           : { type: String, default: "/img/icare-connect-logo1-dark-512.png" },
    domain         : { type: String, default: "" },
    signupCode     : { type: String, default: "" },
    targetUrl      : { type: String, default: null },
    propPageId     : { type: String },
    propLastName   : { type: String },
    propBadgeNumber: { type: String },
    propSecurityKey: { type: String },
    imageSize      : { type: String, default: "128"}
  },
  data() {
    return {
      show         : true,
      loginScreen  : "signin",
      askSignupCode: false,
      domains      : [],
      message      : "",
      pendingUser  : null,
      signedIn     : false,
      tenantKey    : "",
      subDomain    : "",
      lastName     : "",
      badgeNumber  : "",
    };
  },
  computed: {
    resolvedDomain() {
      const fromRoute = this.domain || this.subDomain || "";
      if (fromRoute && fromRoute !== "www" && fromRoute !== "undefined") {
        return fromRoute;
      }
      return "";
    },
    brandHeadline() {
      if (this.domains.length > 0) {
        return "Choose your community";
      }
      if (this.loginScreen === "forgot-password") {
        return "Forgot your password?";
      }
      if (this.loginScreen === "register") {
        return "Need an account?";
      }
      return "Sign in";
    },
    brandLead() {
      if (this.domains.length > 0) {
        return "That email is used at more than one community. Pick the one you want to open.";
      }
      if (this.loginScreen === "forgot-password") {
        return "Enter the email on your account. We’ll send a short code so you can set a new password.";
      }
      if (this.loginScreen === "register") {
        return "Accounts are created by your community. Use the signup code they sent you.";
      }
      return "Use the email and password your community gave you.";
    },
    brandFeatures() {
      if (this.domains.length > 0) {
        return [
          "Choose the community you visit or live in.",
          "You can sign in again later if you picked the wrong one.",
        ];
      }
      if (this.loginScreen === "forgot-password") {
        return [
          "Check your inbox — and junk — for the code.",
          "Enter the code here, then choose a new password.",
          "Sign in with that new password when you’re done.",
        ];
      }
      if (this.loginScreen === "register") {
        return [
          "Ask your community office if you do not have a signup code.",
        ];
      }
      return [
        "Forgot your password? Use the link on this page to get a reset code by email.",
        "Don’t have a login yet? Ask your community office — they invite residents and family.",
        "After you sign in you can see news, charges, and work orders for your household.",
      ];
    },
    brandNote() {
      if (this.loginScreen === "forgot-password") {
        return "Still stuck? Call your community office.";
      }
      return "Need help? Call your community office.";
    }
  },
  async created() {
    initTheme();
    const decode = (value) => {
      const text = value == null ? "" : String(value);
      if (!text) {
        return "";
      }
      try {
        return decodeURIComponent(text);
      } catch (error) {
        return text;
      }
    };
    const rootSub = (this.$root && typeof this.$root.subDomain === "function") ? this.$root.subDomain() : "";
    this.tenantKey = decode(this.$route.params.pageId || this.$route.query.key || this.$route.query.pageId || this.propPageId);
    this.subDomain = decode(this.$route.params.domain || this.$route.query.domain || rootSub);
    this.lastName  = decode(this.$route.params.lastName || this.$route.query.lastName || this.propLastName);
    this.badgeNumber = decode(this.$route.params.badgeNumber || this.$route.query.badgeNumber || this.propBadgeNumber);
  },
  activated() {
    this.show = true;
    initTheme();
  },
  methods: {

    onThemeChange() {},
    close() {
      this.show = false;
      this.$emit( "close" );
      if (!this.isPopup && !this.signedIn) {
        this.$router.replace({ name: "Login" }).catch(() => {});
      }
    },
    ready() {
      this.signedIn = true;
      this.show = false;
      if (this.isPopup) {
        return;
      }
      goToApp(this.$route.query);
    },
    loginCancel() {
      this.domains = [];
      this.message = "";
    },
    selectDomain(domainName) {
      const user = Object.assign({}, this.pendingUser || {}, { domain: domainName });
      this.signIn(user);
    },
    buildCredentials(user) {
      const credentials = Object.assign({}, user);
      credentials.hostUrl = window.location.host;
      credentials.hostName = window.location.hostname;
      const domain = credentials.domain || this.resolvedDomain;
      if (domain && domain !== "www") {
        credentials.domain = domain;
      } else {
        delete credentials.domain;
      }
      return credentials;
    },
    async signIn( user ) {

      if( !user.email || !user.password ) {
        await this.$root.alert( "Please enter your email and password" , "warning" );
        return;
      }

      const credentials = this.buildCredentials(user);
      this.pendingUser = credentials;

      let response;

      try {
        response = await AuthenticationService.login( credentials );

        if( response.status === 428 ) {
          await this.$root.alert( "Verification Code Required.  <p>Please provide the code from your email and try again</p>" , "warning" );
          this.loginScreen   = "signin";
          this.askSignupCode = true;
          return;
        }

        if( response.status === 203 ) {
          this.domains = Array.isArray(response.data) ? response.data : (response.data.domains || []);
          this.message = "Your account is connected to more than one organization. Please select one to continue.";
          return;
        }

        if( response.status===200 ) {
          this.domains = [];
          this.integration = response.data;
          this.$emit( "close" );
          this.$root.integrationLogin( this.integration );

          this.ready();

        } else {
          this.integration = null;
          this.$root.logout();
          await this.$root.alert( "Unable to sign in. Check your details and try again." , "warning" );
        }
      } catch( error ) {
        this.integration = null;
        this.$root.logout();
        await this.$root.alert( "Unable to sign in. Check your details and try again." , "warning" );
        return;
      }

      this.$emit( "login" , this.integration )
    },
    async forgotPassword( user ) {

      if( !user.email) {
        await this.$root.alert( "Please enter a valid email address" , "warning" );
        return;
      }

      const resetSent = "If an account exists for that email, we sent a verification code. Check your spam folder if you do not see it.";

      try {
        const response = await AuthenticationService.forgotPassword( user.email );

        if( response.status === 428 ) {
          await this.$root.alert( "Verification Code Required.  <p>Please provide the code from your email sign in</p>" , "warning" );
          this.loginScreen   = "signin";
          this.askSignupCode = false;
          return;
        }

        await this.$root.alert( resetSent );
        this.askSignupCode = false;
        this.isHaveCode    = true;
      } catch( error ) {
        await this.$root.alert( resetSent );
        this.askSignupCode = false;
        this.isHaveCode    = true;
        return;
      }

    },
    async changePassword( user ) {

      if( !user.email) {
        await this.$root.alert( "Please enter a valid email address" , "warning" );
        return;
      }

      try {
        const response = await AuthenticationService.resetPassword( user );

        if( response.status === 428 ) {
          await this.$root.alert( "Verification Code Required.  <p>Please provide the code from your email sign in</p>" , "warning" );
          this.loginScreen   = "signin";
          this.askSignupCode = false;
          return;
        }

        if( response.status===200 ) {
          await this.$root.alert( "Your password has been reset." );
          this.askSignupCode = false;
          this.isHaveCode    = false;
          this.$router.push( "/login" );
        } else {
          await this.$root.alert( "Unable to reset the password. Check the code and try again." , "warning" );
        }
      } catch( error ) {
        await this.$root.alert( "Unable to reset the password. Check the code and try again." , "warning" );
        return;
      }

    },
    // iCare Connect must attach a portal login to an existing iCare account.
    // Self-serve register is parked until invitation tokens (or an invite record) are ready.
    // async register( user ) {
    //
    //   if( !user.signupCode || !user.email ) {
    //     await this.$root.alert( "Please enter your signup code and email" , "warning" );
    //     return;
    //   }
    //
    //   let result;
    //
    //   try {
    //     user["domain"] = this.resolvedDomain;
    //     user["iType"]  = "icare-connect";
    //     result         = await AuthenticationService.signup( user );
    //
    //     if( result.status == 200 ) {
    //
    //       this.askSignupCode = true;
    //       this.loginScreen   = "signin";
    //       this.$emit( "registerd" , user );
    //       await this.$root.alert( "Check your email for the registration code so you can set your password. Remember to check your spam folder if it is not there." );
    //
    //     } else {
    //       await this.$root.alert( result.data.message , "warning" );
    //     }
    //   } catch( error ) {
    //     console.log( "error" , error.response );
    //     if( error.response && error.response.status == 404 ) {
    //        await this.$root.alert( error.response.data.message , "warning" );
    //     } else {
    //       if( error.response && error.response.data && error.response.data.message ) {
    //         await this.$root.alert( error.response.data.message , "warning" );
    //       } else {
    //         await this.$root.alert( error.message , "warning" );
    //       }
    //     }
    //     return;
    //   }
    //
    // }
  },
};
</script>
<style lang="scss" scoped>
.login-page {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  padding: 1rem 1rem 2rem;
  position: relative;
  background-color: #3a342c;
  background-size: cover;
  background-position: center;
}

.login-page::before {
  content: "";
  position: fixed;
  inset: 0;
  background: rgba(20, 17, 14, 0.48);
  pointer-events: none;
}

.login-page__bar,
.login-shell {
  position: relative;
  z-index: 1;
}

.login-page__bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: min(100%, 1040px);
  margin: 0 auto 1rem;
}

.login-page__home {
  color: #fff8eb;
  font-weight: 500;
  text-decoration: none;
}

.login-page__home:hover { text-decoration: underline; }

.login-shell {
  width: min(100%, 1040px);
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  min-height: 620px;
  background: #fff8eb;
  border-radius: var(--radius-md);
  overflow: hidden;
  border: 2px solid var(--chrome-metal);
}

.login-brand {
  background: var(--chrome);
  color: var(--chrome-muted);
  padding: 2.5rem 2.35rem;
  text-align: left;
  -webkit-font-smoothing: antialiased;
  text-rendering: optimizeLegibility;
}

.login-brand__eyebrow {
  text-transform: uppercase;
  letter-spacing: 0.16em;
  font-size: 0.72rem;
  font-weight: 600;
  margin: 0 0 1rem;
  color: var(--chrome-metal);
}

.login-brand__eyebrow::after {
  content: "";
  display: block;
  width: 3rem;
  height: 2px;
  background: var(--chrome-metal);
  margin-top: 0.7rem;
}

.login-brand__headline {
  margin: 0 0 1rem;
  font-family: var(--font-display);
  font-size: clamp(1.55rem, 2.5vw, 2.05rem);
  font-weight: 500;
  line-height: 1.25;
  letter-spacing: -0.02em;
  color: var(--chrome-ink);
}

.login-brand__lead {
  margin: 0 0 1.75rem;
  color: var(--chrome-muted);
  line-height: 1.72;
  font-weight: 400;
  font-size: 1rem;
}

.login-brand__features {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  gap: 0.95rem;
}

.login-brand__features li {
  position: relative;
  padding-left: 1.15rem;
  color: var(--chrome-ink);
  line-height: 1.5;
  font-weight: 400;
}

.login-brand__features li::before {
  content: "";
  position: absolute;
  left: 0;
  top: 0.55em;
  width: 12px;
  height: 2px;
  background: var(--chrome-metal);
}

.login-brand__trust {
  margin: 2.25rem 0 0;
  font-size: 0.8rem;
  line-height: 1.55;
  color: var(--chrome-muted);
}

.login-panel {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem 1rem;
  background: #fff8eb;
  color: #14110e;
  color-scheme: light;
}

.domain-selection {
  padding: 0.5rem 0.75rem 1rem;
  text-align: center;
  width: 100%;
}

.domain-header {
  margin-bottom: 1rem;

  h4 {
    color: #1b1712;
    font-weight: 600;
    margin-bottom: 0.35rem;
  }

  p {
    color: #5a5348;
    margin-bottom: 0.75rem;
  }
}

.domain-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 0.75rem;
  max-height: min(60vh, 480px);
  overflow-y: auto;
  padding: 0.25rem;
}

.login-page--popup {
  min-height: auto;
  padding: 0;
  background: transparent;
}

.login-page--popup::before {
  display: none;
}

.login-page--popup .login-shell {
  box-shadow: none;
  width: 100%;
  border-radius: 12px;
}

@media (max-width: 800px) {
  .login-shell {
    grid-template-columns: 1fr;
    min-height: auto;
  }

  /* Brand copy stacks above the form on small screens and hides the sign-in fields. */
  .login-brand {
    display: none;
  }

  .login-panel {
    padding: 1.75rem 1.15rem 2rem;
  }
}

</style>

<style lang="scss">
/* Unscoped so html[data-theme] actually matches (Vue scoped would attach the data attr to html). */
.login-page .theme-toggle {
  color: #fff8eb;
  border-color: rgba(255, 248, 235, 0.45);
}

html[data-theme="dark"] .login-page {
  background-color: #3a342c;
}

html[data-theme="dark"] .login-page__home {
  color: #fff8eb;
}

html[data-theme="dark"] .login-shell {
  background: #fff8eb;
  border-color: var(--chrome-metal);
}

html[data-theme="dark"] .login-panel {
  background: #fff8eb;
  color: #14110e;
}

html[data-theme="dark"] .domain-header h4 { color: #1b1712; }
html[data-theme="dark"] .domain-header p { color: #5a5348; }
</style>
